import CustomHtmlKeyboardResponsePlugin from "../../customPlugin/customHtmlKeyboardResponse";

import { KEY_CODES } from "../../gearshiftGame/constants";
import { Game } from "../../gearshiftGame/game";
import { Score } from "../../gearshiftGame/score";
import { createStimulus, hideElement } from "../../gearshiftGame/utils";
import {
  getGearshiftMissionConfig,
  getLayoutVideoUrl,
} from "../../lib/utils";
import { buildFeedbackTrial } from "../trials/feedback";
import { buildInstructionsTrial } from "../trials/instructions";
import { tutorialToggles } from "../../config/tutorialToggles";
import { updateDebugMissionConfig } from "../ui/debugHelpers";
import { buildTutorialWithPhases } from "./utils/buildTutorialWithPhases";
import { buildBasicControlsTutorial } from "./utils/buildBasicControlsTutorial";
import { addFirstTaskMetadata } from "./utils/missionHelpers";
import { buildAdaptiveTrialsNode } from "./utils/adaptiveMissionTrials";
import {
  buildPhase0,
  buildPhase1,
  buildPhase2,
  buildPhase3,
  buildPhase4,
  buildPhase5,
  buildPhase7,
} from "./phases/missionZeroPhases";
import { buildSkipMissionProcedure } from "./skipMission";
import {
  MISSION_ZERO_PAGE_COUNT,
  buildMissionZeroInstructionPages,
} from "./instructionBuilders/missionZero/buildMissionZeroInstructionPages";
import { injectHQMessage, removeHQMessage } from "./hq/buildHQMessage.js";
import { injectSurveyPanel, removeSurveyPanel } from "./hq/buildHQSurveyPanel.js";
import { getHQMessagePanelConfig } from "./hq/localHQMessages.js";

/**
 * @type {number}
 * @description The current mission
 */
const MISSION_NUMBER = 0;

/**
 * @type {Score}
 * @description The user's score
 * NOTE: Used here so we don't have to pass it through every single function
 */
let SCORE;

/**
 * Global counter for tutorial failures in this mission
 */
let tutorialFailureCount = 0;

/**
 * Global failure limit for this mission
 */
let failureLimit = 2;

/**
 * Builds the first mission of the game
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {string} participantID The ID of the participant running the mission
 * @returns The timeline object for the first mission
 */
export async function buildMissionZero(jsPsych, metadata, participantID) {
  // Reset tutorial failure counter when entering this mission
  tutorialFailureCount = 0;

  // Check if a config file was given for this mission
  let config;
  try {
    config = await getGearshiftMissionConfig(jsPsych, metadata, MISSION_NUMBER);
  } catch (e) {
    console.error(`Unable to load mission config file for mission ${MISSION_NUMBER}`);
  }

  // No config file was given for mission 0, skip this mission (no file given or failed to load the file)
  if (!config) return buildSkipMissionProcedure(MISSION_NUMBER);

  // Config file given - build the mission!
  console.log("Mission Zero Configuration:", config);

  // Update debug overlay with mission configuration
  updateDebugMissionConfig(jsPsych, config);

  // Initialize variables shared across the mission
  const {
    missionLives,
    missionMinScore,
    missionMaxCrash,
    missionNumTrials,
    instructionsMaxRetries,
    instructionsAudio,
    trials,
    thisFile,
  } = config;
  // Get the tutorial settings for this mission
  const missionTutorials = {
    ...(tutorialToggles.mission0 ?? {}),
    ...(config.tutorials ?? {}),
    ...(instructionsAudio !== undefined ? { instructionsAudio } : {}),
  };
  // Get the failure limit from the mission config, fallback to tutorial toggles
  failureLimit = instructionsMaxRetries ?? missionTutorials.failsCountLimit ?? 2;
  SCORE = new Score(
    jsPsych,
    missionLives, // # of lives to start the mission with
    metadata.rewardReductionByReset // Amount to reduce score by when the user runs out of lives
  );

  // Adaptive missions: once the static trials below run out, keep requesting more
  // from the backend until a trial with validation.blockType === 98 arrives
  // (signals mission end). Built up front so its `prefetchNextBatch` can also
  // be handed to the last static trial. See adaptiveMissionTrials.js.
  const lastConfiguredTrial = trials[trials.length - 1];
  let adaptiveTrials = null;
  if (config.missionIsAdaptive) {
    const templateTrial =
      [...trials].reverse().find((trial) => trial.trialType === "TASK") ?? lastConfiguredTrial;

    adaptiveTrials = buildAdaptiveTrialsNode({
      jsPsych,
      metadata,
      participantID,
      missionNumber: MISSION_NUMBER,
      initialCursor: {
        block: lastConfiguredTrial?.block ?? 0,
        trialNumber: lastConfiguredTrial?.trialNumber ?? 0,
      },
      templateTrial,
      score: SCORE,
      trialBuilders: {
        TASK: buildMissionZeroTask,
        MESSAGE: buildMissionZeroMessage,
        SURVEY: buildMissionZeroSurvey,
        FEEDBACK: (jsPsych, metadata, trialConfig) =>
          buildMissionZeroFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash),
      },
      createStimulusFn: (trialConfig) => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig),
    });
  }

  // Build the timeline from the config's trials array
  const missionZeroTimeline = trials.map((trialConfig, index) => {
    const stimulus = () => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig);
    // Only the last static trial needs to warm the adaptive queue.
    const prefetchNextBatch =
      index === trials.length - 1 ? adaptiveTrials?.prefetchNextBatch : undefined;
    switch (trialConfig.trialType) {
      case "INSTRUCTION":
        // NOTE: Instructions trial is the first trial of the mission
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionZeroInstructions(
          jsPsych,
          trialConfig,
          metadata,
          stimulus(),
          trials,
          missionTutorials
        );
      case "TASK":
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionZeroTask(jsPsych, metadata, trialConfig, stimulus, prefetchNextBatch);
      case "MESSAGE":
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionZeroMessage(jsPsych, metadata, trialConfig, stimulus);
      case "SURVEY":
        return buildMissionZeroSurvey(jsPsych, metadata, trialConfig, stimulus);
      case "FEEDBACK":
        // NOTE: Feed back trial is the last trial of the mission
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionZeroFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash);
      default:
        throw new Error("An unknown trial type was found:", trialConfig.trialType);
    }
  });

  // Add task_metadata to the first TASK trial
  addFirstTaskMetadata(missionZeroTimeline, trials, metadata);

  if (adaptiveTrials) missionZeroTimeline.push(adaptiveTrials.node);

  return {
    data: {
      missionNumber: MISSION_NUMBER,
      missionFile: thisFile,
      missionNumTrials,
      missionIsAdaptive: !!config.missionIsAdaptive,
      skipped: false,
    },
    save_trial_parameters: { stimulus: false },
    timeline: missionZeroTimeline,
  };
}

/**
 * Builds the trial for displaying the instructions for mission Zero
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The configuration object for a specific trial
 * @param {Object} metadata The global metadata of the experiment
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} allTrials All trials from the mission config (to find a TASK trial for tutorial)
 * @returns
 */
function buildMissionZeroInstructions(
  jsPsych,
  trialConfig,
  metadata,
  stimulus,
  allTrials,
  missionTutorials = {}
) {
  const instructionAudioEnabled = missionTutorials.instructionsAudio ?? tutorialToggles.mission0?.instructionsAudio ?? true;
  const instructionAudio = (src) => (instructionAudioEnabled ? src : undefined);

  const buildMissionZeroInstructionChunkTrial = (start, end, pageOffset) => {
    const chunkTrial = buildInstructionsTrial(jsPsych, [], {
      total_pages: MISSION_ZERO_PAGE_COUNT,
      page_offset: pageOffset,
    });
    const existingOnStart = chunkTrial.on_start;
    chunkTrial.on_start = (trial) => {
      if (typeof existingOnStart === "function") existingOnStart(trial);
      const runtimePages = buildMissionZeroInstructionPages(jsPsych, instructionAudio);
      trial.pages = runtimePages.slice(start, end);
    };
    return chunkTrial;
  };

  // Find the first TASK trial for standard tutorials
  const tutorialConfig = { ...(allTrials.find((t) => t.trialType === "TASK") || trialConfig) };
  tutorialConfig.trialNumber = 1;
  const tutorialStimulus = createStimulus(MISSION_NUMBER, 1, tutorialConfig);
  // Find a different TASK trial with cracking code
  // Use the 7th trial if available, otherwise fallback to first
  const allTaskTrials = allTrials.filter((t) => t.trialType === "TASK");
  let alternativeTutorialConfig = tutorialConfig;
  if (allTaskTrials.length > 6) {
    alternativeTutorialConfig = { ...allTaskTrials[6] };
  } else {
    alternativeTutorialConfig = { ...tutorialConfig };
  }
  alternativeTutorialConfig.trialNumber = 1;
  const alternativeTutorialStimulus = createStimulus(MISSION_NUMBER, 1, alternativeTutorialConfig);

  // Get second TASK trial for second full lane demo
  const nextTutorialConfig = { ...(allTaskTrials[1] || tutorialConfig) };
  nextTutorialConfig.trialNumber = 1;
  const nextTutorialStimulus = createStimulus(MISSION_NUMBER, 1, nextTutorialConfig);

  const totalPages = MISSION_ZERO_PAGE_COUNT;
  const timeline = [];
  const tutorialEnabled = (key, fallback = true) => missionTutorials[key] ?? fallback;

  // Pages 1-4
  if (totalPages > 0) {
    timeline.push(buildMissionZeroInstructionChunkTrial(0, Math.min(4, totalPages), 0));
  }

  // Tutorials after page 4: phases 0 and 1 - DECELERATION (Z key), ACCELERATION (/ key) and car reveal (space bar key)
  if (totalPages >= 4 && tutorialEnabled("decel")) {
    timeline.push(
      buildBasicControlsTutorial(jsPsych, metadata, tutorialConfig, tutorialStimulus, 'decel', buildMissionZeroTaskWithPhasesCustom)
    );
  }
  if (totalPages >= 4 && tutorialEnabled("accel")) {
    timeline.push(
      buildBasicControlsTutorial(jsPsych, metadata, tutorialConfig, tutorialStimulus, 'accel', buildMissionZeroTaskWithPhasesCustom)
    );
  }
  if (totalPages >= 4 && tutorialEnabled("reveal")) {
    timeline.push(
      buildBasicControlsTutorial(jsPsych, metadata, tutorialConfig, tutorialStimulus, 'reveal', buildMissionZeroTaskWithPhasesCustom)
    );
  }

  // Pages 5-8
  if (totalPages > 4) {
    timeline.push(buildMissionZeroInstructionChunkTrial(4, Math.min(8, totalPages), 4));
  }

  // Tutorial after page 8: phases 3,4,5 - FULL LANE SELECTION (space + lane selection)
  if (totalPages >= 8 && tutorialEnabled("fullLane")) {
    timeline.push(
      buildMissionZeroTaskWithPhasesCustom(
        jsPsych,
        metadata,
        tutorialConfig,
        tutorialStimulus,
        [3, 4, 5],
        {
          followUpOnly: true,
          customFollowUpImage: "assets/images/instructions/video_follow_up_2.png",
          customTrialConfigOverrides: {
            stimulusFile: "assets/images/instructions/custom/extras/m0/spacebarFeedbackKkeyM0.png",
            correctKeychar: "k",
            correctKeycode: KEY_CODES.KeyK,
            cueFile: "target_precue_letter.svg",
          },
          tutorialHeader: {
            title: "Practicing Lane Selection (1/3)",
            instruction: "Look at the footer at the bottom and the prompt at the top. Press the specified key"
          },
        }
      )
    );
    // Second tutorial right after full lane (with phases 3,4,5 and next trial)
    timeline.push(
      buildMissionZeroTaskWithPhasesCustom(
        jsPsych,
        metadata,
        nextTutorialConfig,
        nextTutorialStimulus,
        [3, 4, 5],
        {
          skipReadyOverlay: true,
          agentFile: "footer_LaneLabels.svg",
          customTrialConfigOverrides: {
            stimulusFile: "assets/images/instructions/custom/extras/m0/spacebarFeedbackFkeyM0.png",
            correctKeychar: "f",
            correctKeycode: KEY_CODES.KeyF,
            cueFile: "target_precue_letter.svg",
          },
          tutorialHeader: {
            title: "Practicing Lane Selection (2/3)",
            instruction: "Look at the footer at the bottom and the prompt at the top. Press the specified key"
          },
        }
      )
    );
    // Tutorial after page 13: phases 2,3,4,5 - TEST WITHOUT BOMB (uses different trial from array)
    timeline.push(
      buildMissionZeroTaskWithPhasesCustom(
        jsPsych,
        metadata,
        alternativeTutorialConfig,
        alternativeTutorialStimulus,
        [2, 3, 4, 5],
        {
          agentFile: "footer_LaneLabels.svg",
          customTrialConfigOverrides: {
            stimulusFile: "assets/images/instructions/gs01_120px_G_90_6_90_450_set_0004_M0_instructions.png",
            correctKeychar: "s",
            correctKeycode: KEY_CODES.KeyS,
            cueFile: "target_precue_brightness.svg",
          },
          tutorialHeader: {
            title: "Practicing lane selection (3/3)",
            instruction: "Now it’s your turn. Try to crack the code and select the matching lane"
          },
          ready: {
            imageOrVideoUrl: () => getLayoutVideoUrl("instructions/videos/m0/0_solve.mp4"),
          },
        }
      )
    );
  }

  // Pages 9-13 and remaining pages
  if (totalPages > 8) {
    timeline.push(buildMissionZeroInstructionChunkTrial(8, totalPages, 8));
  }

  return {
    data: { trialConfig },
    timeline,
    on_finish: (data) => {
      SCORE.assignScore(data);
    },
  };
}

/**
 * Builds the trial needed for mission one's task
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object|Function} trialConfig The task configuration used to build the trial, or a
 *   getter function returning it. A getter is used by the adaptive trials loop
 *   (see `adaptiveMissionTrials.js`) so the Game instance below is (re)built fresh
 *   from whatever trial is currently queued, instead of once when this function is called.
 * @param {String} stimulus The HTML stimulus to show
 * @param {() => Promise<void>} [prefetchNextBatch] Warms the next adaptive batch from phase 7. See adaptiveMissionTrials.js.
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionZeroTask(jsPsych, metadata, trialConfig, stimulus, prefetchNextBatch) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  // Built lazily (see phase 0's trial_duration below) so a getter-based trialConfig
  // is only read once this trial actually starts running.
  let newGame;
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig: resolveTrialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: () => {
          newGame = new Game(jsPsych, metadata, SCORE, resolveTrialConfig());
          // Exposed globally so aiAgentStateReader (see src/experiment/procedures/aiAgent)
          // can read live game state - tutorial demos already do this themselves
          window._gear_currentGame = newGame;
          return newGame.targetLaunchDelay;
        },
        on_load: () => {
          newGame.startPhase0();
        },
        on_finish: (data) => {
          data.name = "initializeTarget";
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = newGame.driverDistanceToTarget;
          data.trialStart = {
            name: "trialStart",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          };
          SCORE.assignScore(data);
        },
      },

      // Phase 1: Target car begins traveling towards driver car.
      // Terminates when target car/driver car lock in proximity zone
      {
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase1(),
        on_finish: (data) => {
          data.name = "engageTarget";
          data.pulseTTLtrialStart = newGame.ttlPulseFired ? 1 : 0;
          data.TTLpulse_time = newGame.ttlPulseTime;
          data.cueCarOn = {
            name: "cueCarOn",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
            distanceToTarget: newGame.driverDistanceToTarget,
            csi: newGame.csi,
          };

          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      // Phase 2: Start of CSI period, triggered by proximity zone initiation in previous phase
      // Terminates when CSI is over and it's time to show the license
      {
        choices: "NO_KEYS",
        trial_duration: () => newGame.csi,
        on_load: () => newGame.startPhase2(),
        on_finish: (data) => {
          data.name = "licensePlateOn";
          data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
          SCORE.assignScore(data);
        },
      },

      // Phase 3: At the start of this phase, the license is displayed
      // NOTE: The timeout for releaseMechanism 1 and 2 are handled in the Game, not here
      {
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase3(),
        on_finish: (data) => {
          data.name = "responseKeyPressed";
          data.response_keypress_time = data.time_elapsed;
          data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null; // keyCode is null if no response is given
          data.responseTime =
            data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          // displayLicense()/captureLicensePlateOnTime() only run once startPhase3's on_load
          // fires, which is after the "licensePlateOn" (phase 2) row already saved - so this
          // is the earliest row where the value is actually available
          data.license_plate_on_time = newGame.licensePlateOnTime;

          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      // Phase 4: Once lane has been chosen in phase 3, phase 4 begins with the driver car moving to the user chosen lane
      // Terminates when the driver car reaches the chosen lane.
      {
        choices: "NO_KEYS",
        // Start the phase based on the lane chosen
        on_load: () => newGame.startPhase4(jsPsych.data.get().last(1).values()[0].keyCode),
        on_finish: (data) => {
          data.name = "driverInResponseLane";
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },

      //Phase 5
      // Once driver is in chosen lane, the package will appear and travel down the correct lane. Simultaneously, the target car also moves down the highway until it disappears.
      // Once the package reaches the end of the highway, a score will show above the driver car, showing the points the driver car received in this game trial.
      {
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase5(),
        on_finish: (data) => {
          data.name = "packageDisappears";
          data.driverSpeed = newGame.driverCarSpeed;
          data.emotion = newGame.emotion;
          data.packageInTargetLane = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
            packageDistance: newGame.packageDistanceToTarget,
          };
          data.targetDisappears = {
            timestamp: newGame.targetCarDisappearTimestamp,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.emotionReaction = {
            emotion: newGame.emotion,
            driverSpeed: newGame.driverCarSpeed,
            timestamp:
              jsPsych.data.get().last(1).values()[0].time_elapsed - newGame.feedbackDuration,
          };

          SCORE.assignScore(data);
        },
      },

      // Phase 7: Driver car travels back to the center of the highway
      {
        choices: "NO_KEYS",
        on_load: () => {
          newGame.startPhase7();
          prefetchNextBatch?.();
        },
        on_finish: (data) => {
          data.name = "driverAtCenter";
          data.driverToCenter = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Builds the trial needed to display the headquarter message
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object|Function} trialConfig The task configuration used to build the trial, or a
 *   getter function returning it (see `buildMissionZeroTask`'s doc for why).
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionZeroMessage(jsPsych, metadata, trialConfig, stimulus) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  let newGame;
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig: resolveTrialConfig },
    choices: ["Space"],
    on_load: () => {
      const resolvedTrialConfig = resolveTrialConfig();
      newGame = new Game(jsPsych, metadata, SCORE, resolvedTrialConfig);
      // Exposed globally so aiAgentStateReader (see src/experiment/procedures/aiAgent)
      // can read live game state
      window._gear_currentGame = newGame;
      const hqConfig = getHQMessagePanelConfig(MISSION_NUMBER, resolvedTrialConfig.agentFile);
      newGame.startPhase6();
      if (hqConfig) injectHQMessage(jsPsych, hqConfig);
    },
    on_finish: (data) => {
      removeHQMessage();
      SCORE.assignScore(data);
    },
  };
}

/**
 * Builds the trial needed for mission zero's survey
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object|Function} trialConfig The task configuration used to build the trial, or a
 *   getter function returning it (see `buildMissionZeroTask`'s doc for why).
 * @param {String} stimulus The HTML stimulus to show
 * @returns
 */
function buildMissionZeroSurvey(jsPsych, metadata, trialConfig, stimulus) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  let newGame;

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig: resolveTrialConfig },
    timeline: [
      // Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: () => {
          newGame = new Game(jsPsych, metadata, SCORE, resolveTrialConfig());
          // Exposed globally so aiAgentStateReader (see src/experiment/procedures/aiAgent)
          // can read live game state
          window._gear_currentGame = newGame;
          return newGame.targetLaunchDelay;
        },
        on_load: () => newGame.startPhase0(),
      },
      // Phase 6: Headquarter Survey
      {
        on_load: () => {
          newGame.startPhase(6);
          hideElement(newGame.targetCar);
          hideElement(newGame.agentImage);
          newGame.playEffect("message");
          injectSurveyPanel(jsPsych, {
            portrait: "hq-happy",
            promptText: "How are you?",
          });
        },
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        trial_duration: () => newGame.headquarterMessageDuration,
        on_finish: (data) => {
          removeSurveyPanel();
          data.name = "messageFromHQ";
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Builds the trial needed for mission one's feedback
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object|Function} trialConfig The task configuration used to build the trial, or a getter function returning it
 * @param {String} missionMinScore Per mission minimum score to continue
 * @param {String} missionMaxCrash Per mission max crashes allowed
 * @returns
 */
function buildMissionZeroFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  return {
    data: { trialConfig: resolveTrialConfig },
    timeline: [
      // Display the total rewards
      ...buildFeedbackTrial(
        jsPsych,
        SCORE,
        missionMinScore,
        missionMaxCrash,
        "assets/images/postgame/FeedbackScreen_Mission0.svg"
      ),
    ],
  };
}

/**
 * Builds a tutorial task with selectively included phases for mission zero
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} phases Array of phase numbers to include (e.g., [0, 1] or [3, 4, 5])
 * @param {Object} customOptions Optional custom overlay/agentFile/disableAcceleration options
 * @returns A jsPsych node with the tutorial phases
 */
function buildMissionZeroTaskWithPhasesCustom(
  jsPsych,
  metadata,
  trialConfig,
  stimulus,
  phases = [1],
  customOptions = {}
) {
  const buildersMap = {
    0: buildPhase0,
    1: buildPhase1,
    2: buildPhase2,
    3: buildPhase3,
    4: buildPhase4,
    5: buildPhase5,
    7: buildPhase7,
  };

  const { agentFile, tutorialHeader, ...options } = customOptions;

  // Recreate stimulus with tutorialHeader if provided
  let finalStimulus = stimulus;
  if (tutorialHeader) {
    const configForStimulus = {
      ...trialConfig,
      ...(agentFile ? { agentFile } : {}),
      ...(options.customTrialConfigOverrides || {}),
      tutorialHeader,
    };
    finalStimulus = createStimulus(MISSION_NUMBER, 1, configForStimulus);
  }
  // Default overlay options
  const defaultOverlayOptions = {
    ready: {
      forwardText: `[press "__FORWARD_KEY__" twice to start]`,
      backText: `[press "__REPLAY_KEY__" to play video]`,
      imageOrVideoUrl: "assets/images/instructions/video_placeholder.svg",
      showText: false,
    },
    congrats: {
      forwardText: `[press "__FORWARD_KEY__" to continue ->]`,
      backText: "",
      imageOrVideoUrl: "assets/images/instructions/demo_congratulations.png",
      forwardTextCrash: `[press "__FORWARD_KEY__" to try again ]`,
      backTextCrash: "",
      imageOrVideoUrlCrash: "assets/images/instructions/demo_try_again.png",
      showText: false,
    },
  };

  // Merge custom options with defaults (if you want to change each phase's overlay)
  const overlayOptions = {
    ready: { ...defaultOverlayOptions.ready, ...options.ready },
    congrats: { ...defaultOverlayOptions.congrats, ...options.congrats },
    skipReadyOverlay: options.skipReadyOverlay ?? false,
    customFollowUpImage: options.customFollowUpImage ?? null,
    followUpOnly: options.followUpOnly ?? false,
  };

  // Avoid mutating the shared trialConfig because it is reused by later tutorials
  const trialConfigWithOverrides = {
    ...trialConfig,
    ...(agentFile ? { agentFile } : {}),
    ...(options.disableAcceleration !== undefined
      ? { disableAcceleration: options.disableAcceleration }
      : {}),
    ...(options.disableDeceleration !== undefined
      ? { disableDeceleration: options.disableDeceleration }
      : {}),
    ...(options.hideTargetCar !== undefined
      ? { hideTargetCar: options.hideTargetCar }
      : {}),
    ...(options.customTrialConfigOverrides || {}),
  };

  return buildTutorialWithPhases({
    jsPsych,
    metadata,
    score: SCORE,
    trialConfig: trialConfigWithOverrides,
    stimulus: finalStimulus,
    phases,
    buildersMap,
    overlayOptions,
    customOptions,
    hooks: {
      onBeforeCongratsOverlay: (data) => {
        if (data.tryAgain) {
          tutorialFailureCount++;
          console.log(
            `❌ Tutorial failed (attempt ${tutorialFailureCount}/${failureLimit})`
          );
          if (tutorialFailureCount >= failureLimit) {
            // Redirect to support page
            window.location.href = 'supportPage.html';
            return true; // Prevents overlay from showing
          }
        }
        return false;
      },
    },
  });
}

